import React, { useEffect, useState, useRef, useContext } from "react";
import { useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import DatepickerField from "../helpers/DatepickerField";
import { loadConfig } from "../helpers/config";
import { AuthContext } from "../helpers/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import CheckImageWidget from "../components/CheckImageWidget";
import LicenseNumberCard from "../components/LicenseNumberCard";
import PhotoCropModal from "../components/PhotoCropModal";
import { importPrivateKeyFromFile, encryptFile } from "../helpers/licenseImageCrypto";
import { wrapKeyForAllRecipients } from "../helpers/licenseSharing";

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("de-DE");
}

function buildTimeline(license) {
  const checks = (license.checks || []).map((c) => ({
    date: c.check_date,
    action: "Geprüft",
    user: c.checker?.fullName || c.checker?.username || "—",
    notes: c.notes || "—",
  }));
  const logs = (license.logs || []).map((l) => {
    const labelMap = {
      erstellt: "Erstellt",
      bearbeitet: "Bearbeitet",
      status_geändert: "Status geändert",
    };
    let notes = "—";
    if (l.details?.status) {
      notes = `Von „${l.details.status.from}" zu „${l.details.status.to}"`;
    } else if (l.details?.notes) {
      notes = l.details.notes;
    }
    return {
      date: l.timestamp,
      action: labelMap[l.changeType] || l.changeType,
      user: l.inspector,
      notes,
    };
  });
  return [...checks, ...logs].sort((a, b) => new Date(b.date) - new Date(a.date));
}

function License() {
  const { id } = useParams();
  const { authState } = useContext(AuthContext);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiUrl, setApiUrl] = useState("");
  const [editAlert, setEditAlert] = useState(null);
  const [checkAlert, setCheckAlert] = useState(null);
  const [checkDate, setCheckDate] = useState(new Date().toISOString().slice(0, 10));
  const [checkNotes, setCheckNotes] = useState("");
  const [checkSubmitting, setCheckSubmitting] = useState(false);

  // Pending photos for the next check (selected before submission)
  const [pendingFront, setPendingFront] = useState({ file: null, previewUrl: null });
  const [pendingBack, setPendingBack] = useState({ file: null, previewUrl: null });
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  // Raw photo pending crop confirmation: { side, imageSrc, mimeType, fileName }
  const [cropTarget, setCropTarget] = useState(null);

  // Private key for E2EE image decryption (loaded once per page visit)
  const [privateKey, setPrivateKey] = useState(null);
  const [hasServerKey, setHasServerKey] = useState(false);
  const [keyLoadError, setKeyLoadError] = useState(null);
  const keyFileRef = useRef(null);

  useEffect(() => {
    async function fetchData() {
      const config = await loadConfig();
      setApiUrl(config.APIURL);
      try {
        const res = await axios.get(`${config.APIURL}/licenses/${id}`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });
        setLicense(res.data);
        // Check if user has a public key (needed to show photo widgets)
        const pkRes = await axios.get(`${config.APIURL}/auth/public-key`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });
        setHasServerKey(!!pkRes.data.publicKey);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  const handleKeyFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setKeyLoadError(null);
    try {
      const key = await importPrivateKeyFromFile(file);
      setPrivateKey(key);
    } catch {
      setKeyLoadError("Ungültige Schlüsseldatei.");
    } finally {
      if (keyFileRef.current) keyFileRef.current.value = "";
    }
  };

  const editValidationSchema = Yup.object().shape({
    holder_name: Yup.string().required("Pflichtfeld"),
    issue_date: Yup.date().required("Pflichtfeld").typeError("Ungültiges Datum"),
    status: Yup.string().oneOf(["aktiv", "entzogen"]).required("Pflichtfeld"),
  });

  const handleEditSubmit = async (values, { setSubmitting }) => {
    try {
      await axios.put(`${apiUrl}/licenses/${id}`, values, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      // Reload license with fresh checks
      const refreshed = await axios.get(`${apiUrl}/licenses/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      setLicense(refreshed.data);
      setEditAlert({ type: "success", message: "Änderungen gespeichert." });
    } catch (err) {
      setEditAlert({ type: "error", message: err.response?.data?.error || "Fehler beim Speichern" });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoSelect = (side) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Don't store the raw photo yet — open the crop dialog first.
    setCropTarget({
      side,
      imageSrc: URL.createObjectURL(file),
      mimeType: file.type || 'image/jpeg',
      fileName: file.name,
    });
  };

  const clearPhoto = (side) => {
    const setter = side === 'front' ? setPendingFront : setPendingBack;
    const prev = side === 'front' ? pendingFront : pendingBack;
    const ref = side === 'front' ? frontInputRef : backInputRef;
    if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl);
    setter({ file: null, previewUrl: null });
    if (ref.current) ref.current.value = '';
  };

  const handleCropCancel = () => {
    if (cropTarget) {
      URL.revokeObjectURL(cropTarget.imageSrc);
      const ref = cropTarget.side === 'front' ? frontInputRef : backInputRef;
      if (ref.current) ref.current.value = '';
    }
    setCropTarget(null);
  };

  const handleCropConfirm = (blob) => {
    const { side, imageSrc, mimeType, fileName } = cropTarget;
    const setter = side === 'front' ? setPendingFront : setPendingBack;
    const prev = side === 'front' ? pendingFront : pendingBack;
    if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl);
    URL.revokeObjectURL(imageSrc);
    const croppedFile = new File([blob], fileName || 'photo.jpg', { type: mimeType });
    setter({ file: croppedFile, previewUrl: URL.createObjectURL(croppedFile) });
    setCropTarget(null);
  };

  const uploadCheckImage = async (checkId, file, side) => {
    const fileBuffer = await file.arrayBuffer();
    const { ciphertextBase64, ivBase64, aesKey } = await encryptFile(fileBuffer);
    // Automatically share with every user authorized for license access.
    const keys = await wrapKeyForAllRecipients(aesKey, apiUrl, authState.id);
    await axios.post(
      `${apiUrl}/licenses/${id}/check/${checkId}/image`,
      { ciphertext: ciphertextBase64, iv: ivBase64, keys, mimeType: file.type, side },
      { headers: { accessToken: localStorage.getItem('accessToken') } }
    );
  };

  const handleCheck = async () => {
    setCheckSubmitting(true);
    try {
      const checkRes = await axios.post(
        `${apiUrl}/licenses/${id}/check`,
        { check_date: checkDate, notes: checkNotes },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      // Upload photos if selected
      if (hasServerKey && (pendingFront.file || pendingBack.file)) {
        try {
          if (pendingFront.file) await uploadCheckImage(checkRes.data.id, pendingFront.file, 'front');
          if (pendingBack.file) await uploadCheckImage(checkRes.data.id, pendingBack.file, 'back');
        } catch (imgErr) {
          console.error('Bild-Upload fehlgeschlagen:', imgErr);
          setCheckAlert({ type: 'success', message: 'Kontrolle eingetragen, aber Foto-Upload fehlgeschlagen.' });
          clearPhoto('front'); clearPhoto('back');
          const refreshed = await axios.get(`${apiUrl}/licenses/${id}`, { headers: { accessToken: localStorage.getItem('accessToken') } });
          setLicense(refreshed.data);
          setCheckNotes('');
          return;
        }
      }
      const refreshed = await axios.get(`${apiUrl}/licenses/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      setLicense(refreshed.data);
      setCheckNotes("");
      clearPhoto('front');
      clearPhoto('back');
      setCheckAlert({ type: "success", message: "Kontrolle erfolgreich eingetragen." });
    } catch (err) {
      setCheckAlert({ type: "error", message: err.response?.data?.error || "Fehler beim Eintragen" });
    } finally {
      setCheckSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner text="Lade Führerschein..." />;
  if (!license) {
    return (
      <div className="container page-offset-80">
        <div className="alert alert-error">Führerschein nicht gefunden.</div>
      </div>
    );
  }

  const timeline = buildTimeline(license);

  return (
    <div className="container page-offset-80">
      {/* Section 1: Edit form */}
      <div className="card">
        <div className="card-header">Führerschein bearbeiten</div>
        <div className="card-body">
          {editAlert && (
            <div className={`alert alert-${editAlert.type === "success" ? "success" : "error"}`}>
              {editAlert.message}
            </div>
          )}
          <Formik
            initialValues={{
              holder_name: license.holder_name || "",
              issue_date: license.issue_date || "",
              status: license.status || "aktiv",
            }}
            validationSchema={editValidationSchema}
            onSubmit={handleEditSubmit}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="form-group">
                  <label htmlFor="holder_name">Name des Inhabers:</label>
                  <Field id="holder_name" name="holder_name" className="form-control" />
                  <ErrorMessage name="holder_name" component="span" className="error-message" />
                </div>

                <LicenseNumberCard
                  licenseId={id}
                  apiUrl={apiUrl}
                  legacyPlaintext={license.license_number || null}
                  privateKey={privateKey}
                  hasServerKey={hasServerKey}
                  currentUserId={authState.id}
                />

                <div className="form-group">
                  <label htmlFor="issue_date">Ausstellungsdatum:</label>
                  <Field
                    id="issue_date"
                    name="issue_date"
                    component={DatepickerField}
                    className="form-control"
                  />
                  <ErrorMessage name="issue_date" component="span" className="error-message" />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status:</label>
                  <Field id="status" name="status" as="select" className="form-control">
                    <option value="aktiv">Aktiv</option>
                    <option value="entzogen">Entzogen</option>
                  </Field>
                  <ErrorMessage name="status" component="span" className="error-message" />
                </div>

                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? "Speichert..." : "Änderungen speichern"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Private key loader – needed to view/edit the encrypted number and photos */}
      {hasServerKey && (
        <div className="card">
          <div className="card-header">🔑 Private Key</div>
          <div className="card-body">
            {!privateKey ? (
              <>
                <p style={{ fontSize: "0.85rem", margin: "0 0 0.5rem" }}>
                  Wählen Sie Ihre <code>fwminv-private.key</code>-Datei vom USB-Stick, um die
                  verschlüsselte Führerscheinnummer und die Kontrollfotos anzuzeigen.
                  Der Schlüssel wird nur im Arbeitsspeicher gehalten.
                </p>
                {keyLoadError && <p style={{ color: "red", fontSize: "0.8rem", margin: "0.25rem 0" }}>{keyLoadError}</p>}
                <input type="file" accept=".key,.pem" ref={keyFileRef} onChange={handleKeyFileChange}
                  className="form-control" style={{ fontSize: "0.85rem", maxWidth: "360px" }} />
              </>
            ) : (
              <p style={{ fontSize: "0.85rem", color: "#28a745", margin: 0 }}>
                ✅ Private Key geladen{" "}
                <button
                  style={{ fontSize: "0.75rem", background: "none", border: "none", color: "#6c757d", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => setPrivateKey(null)}
                >
                  entfernen
                </button>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Section 2: Quick check action */}
      <div className="card">
        <div className="card-header">Kontrolle durchführen</div>
        <div className="card-body">
          {checkAlert && (
            <div className={`alert alert-${checkAlert.type === "success" ? "success" : "error"}`}>
              {checkAlert.message}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="check_date">Kontrolldatum:</label>
            <input
              id="check_date"
              type="date"
              className="form-control"
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="check_notes">Notizen (optional):</label>
            <textarea
              id="check_notes"
              className="form-control"
              rows={3}
              value={checkNotes}
              onChange={(e) => setCheckNotes(e.target.value)}
              placeholder="Bemerkungen zur Kontrolle..."
            />
          </div>
          {/* Optional front + back photos */}
          {!hasServerKey && (
            <p style={{ fontSize: '0.8rem', color: '#6c757d', margin: '0.5rem 0' }}>
              ℹ️ Um Fotos hochzuladen oder anzuzeigen, muss zuerst ein{' '}
              <a href="/profile">Verschlüsselungsschlüssel im Profil erstellt</a> werden.
            </p>
          )}
          {hasServerKey && (
            <div className="form-group">
              <label>Fotos (optional):</label>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {['front', 'back'].map((side) => {
                  const pending = side === 'front' ? pendingFront : pendingBack;
                  const ref = side === 'front' ? frontInputRef : backInputRef;
                  const label = side === 'front' ? 'Vorderseite' : 'Rückseite';
                  return (
                    <div key={side} style={{ flex: '1 1 140px', minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem', color: '#495057' }}>{label}</div>
                      {!pending.previewUrl ? (
                        <label style={{ display: 'block', padding: '0.45rem 0.6rem', background: '#0d6efd', color: '#fff', borderRadius: '4px', cursor: checkSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.82rem', textAlign: 'center' }}>
                          📷 Aufnehmen
                          <input type="file" accept="image/*" capture="environment" ref={ref}
                            style={{ display: 'none' }} onChange={handlePhotoSelect(side)} disabled={checkSubmitting} />
                        </label>
                      ) : (
                        <div>
                          <img src={pending.previewUrl} alt={label}
                            style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '4px', display: 'block', marginBottom: '0.3rem' }} />
                          <button type="button"
                            style={{ fontSize: '0.75rem', background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: 0 }}
                            onClick={() => clearPhoto(side)} disabled={checkSubmitting}>
                            ✕ Entfernen
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <button
            className="btn btn-success"
            disabled={checkSubmitting || !checkDate}
            onClick={handleCheck}
          >
            {checkSubmitting ? "Wird eingetragen..." : "✔ Kontrolle eintragen"}
          </button>
        </div>
      </div>

      {/* Section 2b: Kontrollen mit Fotos */}
      {!hasServerKey && (license.checks || []).length > 0 && (
        <div className="card">
          <div className="card-header">Kontrollen – Fotos</div>
          <div className="card-body">
            <p style={{ fontSize: '0.875rem', color: '#6c757d', margin: 0 }}>
              ℹ️ Kein Verschlüsselungsschlüssel vorhanden. Bitte erstellen Sie zuerst einen{' '}
              <a href="/profile">Schlüssel im Profil</a>, um Kontrollfotos hochzuladen oder anzuzeigen.
            </p>
          </div>
        </div>
      )}
      {hasServerKey && (license.checks || []).length > 0 && (
        <div className="card">
          <div className="card-header">Kontrollen – Fotos</div>
          <div className="card-body">
            {!privateKey && (
              <p style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: "0.75rem" }}>
                ℹ️ Bitte oben den Private Key laden, um die Fotos anzuzeigen.
              </p>
            )}

            <div className="table-responsive">
              <table className="table table-stack">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Prüfer</th>
                    <th>Notizen</th>
                    <th>Foto</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(license.checks || [])].sort((a, b) => new Date(b.check_date) - new Date(a.check_date)).map((check) => (
                    <tr key={check.id}>
                      <td data-label="Datum" style={{ whiteSpace: "nowrap" }}>{formatDateTime(check.check_date)}</td>
                      <td data-label="Prüfer">{check.checker?.fullName || check.checker?.username || "—"}</td>
                      <td data-label="Notizen">{check.notes || "—"}</td>
                      <td data-label="Foto">
                        <CheckImageWidget
                          licenseId={id}
                          checkId={check.id}
                          apiUrl={apiUrl}
                          privateKey={privateKey}
                          hasServerKey={hasServerKey}
                          viewOnly
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Section 3: Audit log */}
      <div className="card">
        <div className="card-header">Protokoll / Verlauf</div>
        <div className="card-body">
          {timeline.length === 0 ? (
            <div className="empty-state">
              <p>Keine Einträge vorhanden.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-stack">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Aktion</th>
                    <th>Benutzer</th>
                    <th>Notizen</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.map((entry, idx) => (
                    <tr key={idx}>
                      <td data-label="Datum">{formatDateTime(entry.date)}</td>
                      <td data-label="Aktion">{entry.action}</td>
                      <td data-label="Benutzer">{entry.user}</td>
                      <td data-label="Notizen">{entry.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {cropTarget && (
        <PhotoCropModal
          imageSrc={cropTarget.imageSrc}
          mimeType={cropTarget.mimeType}
          title={cropTarget.side === 'front' ? 'Vorderseite zuschneiden' : 'Rückseite zuschneiden'}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
}

export default License;
